import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject } from '@angular/core';
import { ClipboardService } from 'ngx-clipboard';
import { SnackBarService } from '../services/snackbar.service';
import { Utils } from '../utils/utils';
import { BridgeAdUnit } from '../domain/bridge-ad-unit.model';
import { DevicePlacement } from '../domain/device-placement.model';
import { BidderConfig } from '../domain/bidder-config.model';

@Component({
  selector: 'cm-bidder-config-preview-dialog',
  template: `
    <h3 class="mat-dialog-title">
      Prebid config parameters
    </h3>
    <mat-dialog-content>
      <div *ngFor="let config of configs" class=" mb-2">
        <mat-form-field class="w-100">
          <textarea matInput matTextareaAutosize rows="6" [placeholder]="getPlaceholderFromConfig(config)"
                    [value]="getUserFriendlyConfig(config)" readonly class="textarea-note">
          </textarea>
        </mat-form-field>
      </div>
      <div *ngIf="configs.length === 0">
        <p><i class="far fa-frown" aria-hidden="true"></i> We are sorry but there are no active configurations at this moment.</p>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions class="justify-content-center">
      <button mat-raised-button color="primary" *ngIf="configs.length != 0" (click)="onDownloadFile()" class="iconed  mb-3 mb-sm-0">
        <i class="fas fa-download"></i>
        <span>Download file</span>
      </button>
      <button mat-raised-button color="primary" *ngIf="configs.length != 0" (click)="copyToClipboard()" class="iconed mb-3 mb-sm-0">
        <i class="fas fa-clipboard"></i>
        <span>Copy all to clipboard</span>
      </button>
      <button mat-raised-button color="" class="mr-1" [mat-dialog-close]="false">Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .mat-input-element.textarea-note {
      min-height: 220px;
      overflow: auto;
    }

    ::ng-deep .mat-form-field-appearance-legacy .mat-form-field-underline {
      display: none;
    }
  `]
})
export class BidderConfigPreviewDialogComponent {

  configs: any[];
  private joinedConfigs: string;

  adUnitMap: Map<string, BridgeAdUnit> = new Map<string, BridgeAdUnit>();

  constructor(public dialogRef: MatDialogRef<BidderConfigPreviewDialogComponent>,
              public snackBarService: SnackBarService,
              private clipboardService: ClipboardService,
              @Inject(MAT_DIALOG_DATA) public data: { config: BidderConfig[], adUnits: BridgeAdUnit[] }) {
    this.configs = BidderUtils.transformConfigs(data.config);
    if (data.adUnits) {
      data.adUnits.forEach(unit => this.adUnitMap.set(unit.campaignId, unit));
    }
    this.joinedConfigs = this.configs.map(c => JSON.stringify(c)).join(',\n');
  }

  getUserFriendlyConfig(config) {
    return BidderUtils.getUserFriendlyConfig(config);
  }

  getPlaceholderFromConfig(config) {
    return BidderUtils.getPlaceholderFromConfig(config, this.adUnitMap);
  }

  copyToClipboard() {
    if (this.configs) {
      this.clipboardService.copyFromContent(this.joinedConfigs);
      this.snackBarService.success('Copied to clipboard!');
    }
  }

  onDownloadFile() {
    BidderUtils.downloadConfigs(this.configs, this.adUnitMap);
  }

}

export class BidderUtils {
  static getUserFriendlyConfig(config) {

    const prettyJson = JSON.stringify(config.params, null, '  ');
    return `bidder: 'consumable',\n${prettyJson}`.replace(/\"([^(\")"]+)\":/g, '$1:')
      .replace(new RegExp('\"', 'g'), '\'');
  }

  static getPlaceholderFromConfig(config: any, adUnitMap: Map<string, BridgeAdUnit>) {
    try {
      let result = new RegExp(/((\d{2,3})(x)(\d{2,3}))/g).exec(config.params.unitName);
      if (result) {
        const unit = <BridgeAdUnit> adUnitMap.get(config.params.unitId);
        const placement = unit ? unit.devicePlacement === DevicePlacement.MW ? 'mobile' : 'desktop' : '';
        return `Ad slot size: ${result[0]} ${placement}`;
      } else {
        result = new RegExp(/(responsive)/g).exec(config.params.unitName);
        if (result) {
          return 'Ad slot size: responsive';
        } else {
          return 'Ad slot size: responsive'; //audio footer?
        }
      }
    } catch (e) {
    }
  }

  static downloadConfigs(configs, adUnitMap: Map<string, BridgeAdUnit>) {
    const fileName = 'bidder_config' + '_' + new Date().toLocaleDateString();
    let fileContent = '';
    configs.forEach((item, index) => {
      fileContent += this.getPlaceholderFromConfig(item, adUnitMap) + ' - ' + this.getUserFriendlyConfig(item);
      // add new row sign if multiple configs should be downloaded into the same file
      if ((index + 1) < configs.length) {
        fileContent += '\r\n';
      }
    });

    Utils.exportBlob([fileContent], fileName);
  }

  static transformConfigs(configs: BidderConfig[]) {
    return configs
      .map(item => {
        item.zoneIds.forEach((value, index, arr) => {
          const zoneIdNumber = parseInt(value, 10);
          arr[index] = isNaN(zoneIdNumber) ? value : zoneIdNumber;
        });
        return item;
      })
      .map(config => ({
        bidder: 'consumable',
        params: config
      }));
  }
}
